import api from './api';

const reportService = {
  /**
   * Obtener reporte de Kardex
   * @param {number|null} warehouseId - Si se envía, el reporte solo incluye stock y datos del almacén indicado
   */
  getKardexReport: async (warehouseId = null) => {
    try {
      const params = {};
      if (warehouseId) params.warehouse_id = warehouseId;
      const response = await api.get('/reports/kardex', { params });
      return response.data;
    } catch (error) {
      console.error('Error al obtener reporte de kardex:', error);
      throw error;
    }
  },

  /**
   * Obtener detalle de kardex para un producto en un almacén
   * @param {string} productId - ID del producto
   * @param {string} warehouseId - ID del almacén
   * @param {number} year - Año (opcional)
   * @param {number} month - Mes (1-12, opcional)
   * @param {boolean} fromMonth - Ver a partir del mes seleccionado (opcional)
   * @param {boolean} allMovements - Obtener todos los movimientos sin filtrar (opcional)
   * @param {boolean} recalculate - Solo para recálculo: desde el mes indicado hasta el último movimiento (opcional)
   */
  getKardexDetail: async (productId, warehouseId, year = null, month = null, fromMonth = false, allMovements = false, recalculate = false) => {
    try {
      const params = { product_id: productId, warehouse_id: warehouseId };
      if (year) params.year = year;
      if (month) params.month = month;
      if (fromMonth) params.from_month = 'true';
      if (allMovements) params.all_movements = 'true';
      if (recalculate) params.recalculate = 'true';
      
      const response = await api.get('/reports/kardex/detail', { params });
      return response.data;
    } catch (error) {
      console.error('Error al obtener detalle de kardex:', error);
      throw error;
    }
  },

  /**
   * Obtener todos los kardex de todos los productos con todos los movimientos hasta el mes actual
   */
  getAllKardexWithMovements: async (warehouseId = null) => {
    try {
      const params = {};
      if (warehouseId) params.warehouse_id = warehouseId;
      const response = await api.get('/reports/kardex/all-with-movements', { params });
      return response.data;
    } catch (error) {
      console.error('Error al obtener todos los kardex con movimientos:', error);
      throw error;
    }
  },

  /**
   * Obtener solo producto-almacén con inconsistencias de kardex (costo incorrecto, stock negativo, etc.)
   */
  getKardexInconsistencies: async () => {
    try {
      const response = await api.get('/reports/kardex/inconsistencies');
      return response.data;
    } catch (error) {
      console.error('Error al obtener inconsistencias de kardex:', error);
      throw error;
    }
  },

  getKardexInconsistenciesScanMeta: async () => {
    try {
      const response = await api.get('/reports/kardex/inconsistencies/scan-meta');
      return response.data;
    } catch (error) {
      console.error('Error al obtener meta de escaneo kardex:', error);
      throw error;
    }
  },

  scanKardexInconsistenciesBatch: async ({ offset = 0, batchSize = 200, totalPairs = 0 } = {}) => {
    try {
      const response = await api.get('/reports/kardex/inconsistencies/scan', {
        params: {
          offset,
          batch_size: batchSize,
          total_pairs: totalPairs,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error al escanear lote de inconsistencias:', error);
      throw error;
    }
  },

  /**
   * Plan de movimientos a recalcular para un producto-almacén (servidor).
   */
  getKardexRecalculatePlan: async (productId, warehouseId) => {
    try {
      const response = await api.get('/reports/kardex/recalculate-plan', {
        params: { product_id: productId, warehouse_id: warehouseId },
      });
      return response.data;
    } catch (error) {
      console.error('Error al obtener plan de recálculo de kardex:', error);
      throw error;
    }
  },

  /**
   * Dashboard de inventario (Almacenes)
   */
  getInventoryDashboard: async (params = {}) => {
    try {
      const response = await api.get('/reports/inventory/dashboard', { params });
      return response.data;
    } catch (error) {
      console.error('Error al obtener dashboard de inventario:', error);
      throw error;
    }
  },

  getEntryChangeAlertsReport: async (params = {}) => {
    try {
      const response = await api.get('/reports/entry-change-alerts', { params });
      return response.data;
    } catch (error) {
      console.error('Error al obtener reporte de cambio de entradas:', error);
      throw error;
    }
  },

  previewRecalculateAllKardex: async ({ warehouseId = null, onlyInconsistent = true, pairs = null } = {}) => {
    try {
      const payload = { only_inconsistent: onlyInconsistent };
      if (warehouseId) payload.warehouse_id = warehouseId;
      if (Array.isArray(pairs)) payload.pairs = pairs;

      const response = Array.isArray(pairs)
        ? await api.post('/reports/kardex/recalculate-all/preview', payload)
        : await api.get('/reports/kardex/recalculate-all/preview', {
          params: { ...payload, only_inconsistent: onlyInconsistent ? 'true' : 'false' },
        });
      return response.data;
    } catch (error) {
      console.error('Error al obtener vista previa de recálculo masivo:', error);
      throw error;
    }
  },

  recalculateAllKardex: async ({ warehouseId = null, onlyInconsistent = true, pairs = null } = {}) => {
    try {
      const payload = { only_inconsistent: onlyInconsistent };
      if (warehouseId) payload.warehouse_id = warehouseId;
      if (Array.isArray(pairs)) payload.pairs = pairs;
      const response = await api.post('/reports/kardex/recalculate-all', payload);
      return response.data;
    } catch (error) {
      console.error('Error al recalcular kardex masivamente:', error);
      throw error;
    }
  },

  /**
   * Obtener reporte de Entradas y Salidas
   * @param {number} year - Año
   * @param {number} month - Mes (1-12)
   * @param {string} warehouseId - ID del almacén (opcional)
   * @param {boolean} fromStart - Ver desde el inicio (opcional)
   */
  getEntriesExitsReport: async (year, month, warehouseId = null, fromStart = false) => {
    try {
      const params = { year, month };
      if (warehouseId) params.warehouse_id = warehouseId;
      if (fromStart) params.from_start = 'true';

      const response = await api.get('/reports/entries-exits', { params });
      return response.data;
    } catch (error) {
      console.error('Error al obtener reporte de entradas y salidas:', error);
      throw error;
    }
  },

  /**
   * Matriz Entradas y Salidas: consolida todas las guías en un rango de meses.
   */
  getEntriesExitsMatrixReport: async ({
    monthFrom,
    yearFrom,
    monthTo,
    yearTo,
    warehouseId = null,
  }) => {
    try {
      const params = {
        month_from: monthFrom,
        year_from: yearFrom,
        month_to: monthTo,
        year_to: yearTo,
      };
      if (warehouseId) params.warehouse_id = warehouseId;

      const response = await api.get('/reports/entries-exits-matrix', { params });
      return response.data;
    } catch (error) {
      console.error('Error al obtener matriz de entradas y salidas:', error);
      throw error;
    }
  },

  /**
   * Obtener reporte de Resultados (Administración)
   * @param {object} params - mode: 'by_period' | 'by_milestones', date_from, date_to (periodo), milestone_ids[] (hitos), warehouse_id (opcional)
   */
  getResultsReport: async (params = {}) => {
    try {
      const response = await api.get('/reports/results', { params });
      return response.data;
    } catch (error) {
      console.error('Error al obtener reporte de resultados:', error);
      throw error;
    }
  },

  /**
   * Detalle de salidas del reporte Resultados (productos de almacén) para el período filtrado.
   * @param {object} params - mismos que getResultsReport: mode, date_from, date_to, milestone_ids[], project_id
   */
  getResultsExitsDetail: async (params = {}) => {
    try {
      const response = await api.get('/reports/results/exits-detail', { params });
      return response.data;
    } catch (error) {
      console.error('Error al obtener detalle de salidas:', error);
      throw error;
    }
  },

  /**
   * Obtener estadísticas del Dashboard
   */
  getDashboardStats: async () => {
    try {
      const response = await api.get('/dashboard/stats');
      return response.data;
    } catch (error) {
      console.error('Error al obtener estadísticas del dashboard:', error);
      throw error;
    }
  },

  /**
   * Sustento de documentos del gráfico Productos/Servicios por mes y año.
   * @param {number} year
   * @param {number} month
   */
  getDashboardSupportDocuments: async (year, month) => {
    try {
      const response = await api.get('/dashboard/stats/support-documents', {
        params: { year, month },
      });
      return response.data;
    } catch (error) {
      console.error('Error al obtener sustento del dashboard:', error);
      throw error;
    }
  },

  /**
   * Obtener reporte de salidas por entidad (proveedor)
   * @param {string} supplierId - ID del proveedor
   * @param {string|null} warehouseId - ID del almacén (opcional)
   */
  getExitsBySupplierReport: async (supplierId, warehouseId = null) => {
    try {
      const params = { supplier_id: supplierId };
      if (warehouseId) params.warehouse_id = warehouseId;
      const response = await api.get('/reports/exits-by-supplier', { params });
      return response.data;
    } catch (error) {
      console.error('Error al obtener reporte de salidas por proveedor:', error);
      throw error;
    }
  },

  /**
   * Obtener trazabilidad de documentos
   * @param {object} filters - Filtros: document_id, document_code, supplier_id, date_from, date_to
   */
  getDocumentTraceability: async (filters = {}) => {
    try {
      const response = await api.get('/reports/document-traceability', {
        params: filters
      });
      return response.data;
    } catch (error) {
      console.error('Error al obtener trazabilidad de documentos:', error);
      throw error;
    }
  },

  /**
   * Obtener trazabilidad desde Órdenes de Compra
   * @param {object} filters - Filtros: purchase_order_id, purchase_order_code, supplier_id, date_from, date_to
   */
  getPurchaseOrderTraceability: async (filters = {}) => {
    try {
      const response = await api.get('/reports/purchase-order-traceability', {
        params: filters
      });
      return response.data;
    } catch (error) {
      console.error('Error al obtener trazabilidad de órdenes de compra:', error);
      throw error;
    }
  },

  /**
   * Obtener trazabilidad desde Órdenes de Servicio
   * @param {object} filters - Filtros: service_order_id, service_order_code, supplier_id, date_from, date_to
   */
  getServiceOrderTraceability: async (filters = {}) => {
    try {
      const response = await api.get('/reports/service-order-traceability', {
        params: filters
      });
      return response.data;
    } catch (error) {
      console.error('Error al obtener trazabilidad de órdenes de servicio:', error);
      throw error;
    }
  },

  /**
   * Obtener trazabilidad desde Guías de Entrada
   * @param {object} filters - Filtros: entry_guide_id, entry_guide_code, warehouse_id, guide_type, date_from, date_to
   */
  getEntryGuideTraceability: async (filters = {}) => {
    try {
      const response = await api.get('/reports/entry-guide-traceability', {
        params: filters
      });
      return response.data;
    } catch (error) {
      console.error('Error al obtener trazabilidad de guías:', error);
      throw error;
    }
  },

  getRequirementTraceability: async (filters = {}) => {
    try {
      const response = await api.get('/reports/requirement-traceability', { params: filters });
      return response.data;
    } catch (error) {
      console.error('Error al obtener trazabilidad de requerimientos:', error);
      throw error;
    }
  },
};

export default reportService;

